import { interpolateClipKeyframes, type LayerPose } from '@/animation/interpolateClipKeyframes'
import type { AnimationLayer } from '@/types/animationLayer'

function clonePose(p: LayerPose): LayerPose {
  return {
    position: { ...p.position },
    rotationDeg: { ...p.rotationDeg },
    scale: { ...p.scale },
    materialOpacity: p.materialOpacity,
  }
}

/**
 * 在给定全局时刻求图层姿态：clip 区间内线插值；clip 前用图层静态变换；clip 间隙与 clip 之后 **保持上一段 clip 末端姿态**。
 *
 * @param layer - 动画图层（`clips` 同层互不重叠，未排序时会内部排序）
 * @param globalTimeSeconds - 全局时间轴时刻（秒）
 */
export function evaluateLayerPoseAtGlobalTime(
  layer: AnimationLayer,
  globalTimeSeconds: number,
): LayerPose {
  const t = Number(globalTimeSeconds)
  const staticPose: LayerPose = {
    position: { ...layer.position },
    rotationDeg: { ...layer.rotationDeg },
    scale: { ...layer.scale },
    /** 片段外静态时刻：未处于任一片段内时按 1（图片平面见规范） */
    materialOpacity: 1,
  }

  const clipsSorted = [...layer.clips].sort((a, b) => a.startSec - b.startSec)
  if (clipsSorted.length === 0) return staticPose

  if (t < clipsSorted[0]!.startSec) return staticPose

  let prevEndPose: LayerPose = staticPose

  for (const c of clipsSorted) {
    if (t < c.startSec) {
      return clonePose(prevEndPose)
    }
    const end = c.startSec + c.durationSec
    const local = Math.min(c.durationSec, Math.max(0, t - c.startSec))
    if (t <= end + 1e-9) {
      return interpolateClipKeyframes(c, local, prevEndPose)
    }
    prevEndPose = interpolateClipKeyframes(c, c.durationSec, prevEndPose)
  }

  return clonePose(prevEndPose)
}

export type { LayerPose }
